import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errors";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCancelAccountDeletion,
  useRequestAccountDeletion,
  useUpdateProfile,
  useUser,
} from "@/queries/user";
import { supabase } from "@/utils/supabase";
import { BlurredEmail } from "@/components/blurred-email";

export function SettingsPage() {
  const user = useUser();
  const updateProfile = useUpdateProfile();
  const requestDeletion = useRequestAccountDeletion();
  const cancelDeletion = useCancelAccountDeletion();
  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [email, setEmail] = useState("");

  const loaded = user.data;
  const obscureEmail = loaded?.obscure_email ?? false;
  const nameValue = displayName || loaded?.display_name || "";
  const avatarValue = avatarUrl || loaded?.avatar_url || "";
  const emailValue = email || loaded?.email || "";

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    const nextEmail = emailValue.trim();
    if (!nextEmail) return;

    const { error } = await supabase.auth.updateUser({ email: nextEmail });
    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success("Email update requested. Check your inbox to confirm it.");
    setEmail("");
  }

  async function submitProfile(e: React.FormEvent) {
    e.preventDefault();
    const avatar = avatarValue.trim();
    if (avatar && !/^(https?:\/\/|data:image\/)/i.test(avatar)) {
      toast.error("Avatar URL must start with http(s):// or data:image/");
      return;
    }
    updateProfile.mutate(
      {
        display_name: nameValue.trim(),
        avatar_url: avatar || null,
      },
      {
        onSuccess: () => {
          toast.success("Profile updated");
          setDisplayName("");
          setAvatarUrl("");
        },
        onError: (error) => toast.error(getErrorMessage(error)),
      },
    );
  }

  const pendingDeletion = loaded?.pending_deletion_at
    ? new Date(loaded.pending_deletion_at)
    : null;

  async function handleRequestDelete() {
    if (
      !confirm(
        "Mark your account for deletion? You'll have 60 days to cancel before all data is removed.",
      )
    ) {
      return;
    }
    requestDeletion.mutate(undefined, {
      onSuccess: (res) => {
        const when = new Date(res.pending_deletion_at).toLocaleDateString();
        toast.success(`Account scheduled for deletion on ${when}`);
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    });
  }

  async function handleCancelDelete() {
    cancelDeletion.mutate(undefined, {
      onSuccess: () => toast.success("Deletion cancelled"),
      onError: (error) => toast.error(getErrorMessage(error)),
    });
  }

  return (
    <div className="space-y-6">
      <Helmet><title>TodoLLM - Account</title></Helmet>
      <header className="space-y-1">
        <h2 className="text-3xl font-semibold tracking-tight">Account settings</h2>
        <p className="text-sm text-muted-foreground">
          Update your profile, change your email address, and manage account access.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>
            Display name and profile image are shown around the dashboard.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submitProfile}>
            <div className="grid gap-4 sm:grid-cols-[96px_1fr] sm:items-start">
              <ProfilePreview name={nameValue || emailValue || "User"} avatarUrl={avatarValue || null} />
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="display-name">Display name</Label>
                  <Input
                    id="display-name"
                    value={nameValue}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Shawn"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="avatar-url">Profile picture URL</Label>
                  <Input
                    id="avatar-url"
                    value={avatarValue}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                    placeholder="https://example.com/avatar.png"
                  />
                </div>
              </div>
            </div>
            <Button type="submit" disabled={updateProfile.isPending || !loaded}>
              {updateProfile.isPending ? "Saving..." : "Save profile"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email address</CardTitle>
          <CardDescription>
            Supabase may require email confirmation before the new address becomes active.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loaded?.email && (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Current email</p>
              <BlurredEmail
                email={loaded.email}
                displayName={loaded.display_name}
                active={obscureEmail}
                className="text-sm"
              />
            </div>
          )}
          <form className="space-y-4" onSubmit={submitEmail}>
            <div className="space-y-2">
              <Label htmlFor="email">New email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <Button type="submit" disabled={!loaded}>
              Update email
            </Button>
          </form>
          <div className="flex items-center gap-3 pt-2 border-t">
            <input
              id="obscure-email"
              type="checkbox"
              checked={obscureEmail}
              onChange={(e) =>
                updateProfile.mutate(
                  { obscure_email: e.target.checked },
                  { onError: (error) => toast.error(getErrorMessage(error)) },
                )
              }
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <Label htmlFor="obscure-email" className="cursor-pointer font-normal">
              Blur email addresses across the app
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle>Danger zone</CardTitle>
          <CardDescription>
            {pendingDeletion
              ? `Account scheduled for deletion on ${pendingDeletion.toLocaleDateString()}. Cancel below to keep your account.`
              : "Request account deletion. You have 60 days to change your mind; after that, all data is removed."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingDeletion ? (
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={cancelDeletion.isPending || !loaded}
            >
              {cancelDeletion.isPending ? "Cancelling…" : "Cancel deletion"}
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={handleRequestDelete}
              disabled={requestDeletion.isPending || !loaded}
            >
              {requestDeletion.isPending ? "Requesting…" : "Request account deletion"}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProfilePreview({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl: string | null;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="h-24 w-24 rounded-2xl border border-border object-cover"
      />
    );
  }

  return (
    <div className="flex h-24 w-24 items-center justify-center rounded-2xl border border-border bg-muted text-2xl font-semibold">
      {initials || "U"}
    </div>
  );
}
